<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <h6>Permisos de Usuario - {{ user ? user.name : 'Cargando...' }}</h6>
          </div>
          <div class="card-body px-4 pt-2 pb-4">
            <div v-if="error" class="alert alert-danger">
              {{ error }}
            </div>
            
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Cargando...</span>
              </div>
              <p class="mt-2">Cargando permisos...</p>
            </div>
            
            <div v-else>
              <div class="permission-header d-flex align-items-center mb-3">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="selectAll" 
                    :checked="allSelected"
                    @change="toggleAllPermissions" 
                  >
                  <label class="form-check-label ms-2 fw-bold" for="selectAll">Seleccionar todos los módulos</label>
                </div>
              </div>
              
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Módulo</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-center">Acceso</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="module in availableModules" :key="module.id">
                      <td>
                        <div class="d-flex align-items-center px-2">
                          <i :class="module.icon" class="me-3 text-lg"></i>
                          <div class="d-flex flex-column">
                            <h6 class="mb-0 text-sm">{{ module.name }}</h6>
                            <p class="text-xs text-secondary mb-0">{{ module.code }}</p>
                          </div>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="form-check form-switch d-flex justify-content-center">
                          <input 
                            class="form-check-input" 
                            type="checkbox" 
                            :id="`module-${module.id}`" 
                            v-model="selectedPermissions[module.id]"
                            :disabled="saving"
                            @change="updatePermission(module.id)"
                          >
                        </div>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">{{ module.description }}</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div v-if="saveMessage" :class="`alert ${saveSuccess ? 'alert-success' : 'alert-danger'} mt-3`">
                {{ saveMessage }}
              </div>
              
              <div class="d-flex justify-content-end mt-4">
                <button 
                  class="btn btn-success" 
                  @click="saveAllPermissions" 
                  :disabled="saving || !permissionsChanged"
                >
                  <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  {{ saving ? 'Guardando...' : 'Guardar Todos los Cambios' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "UserPermissions",
  props: {
    userId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      user: null,
      loading: true,
      saving: false,
      error: null,
      saveMessage: null,
      saveSuccess: false,
      availableModules: [
        { 
          id: 'dashboard', 
          name: 'Panel Principal', 
          code: 'dashboard',
          icon: 'ri-dashboard-line',
          description: 'Acceso al panel principal de la aplicación' 
        },
        { 
          id: 'bulk_update', 
          name: 'Actualización Masiva', 
          code: 'bulk_update',
          icon: 'ri-upload-2-line',
          description: 'Realizar actualizaciones masivas de datos' 
        },
        { 
          id: 'bulk_loads', 
          name: 'Cargas Másivas', 
          code: 'bulk_loads',
          icon: 'ri-upload-cloud-line',
          description: 'Gestión de cargas masivas de datos' 
        },
        { 
          id: 'celsig5', 
          name: 'CelSig5 y SCeGR', 
          code: 'celsig5',
          icon: 'ri-settings-line',
          description: 'Acceso a los módulos CelSig5 y SCeGR' 
        },
        { 
          id: 'connectivity', 
          name: 'CON - Conectividad', 
          code: 'connectivity',
          icon: 'ri-wifi-line',
          description: 'Gestión de conectividad' 
        },
        { 
          id: 'locations', 
          name: 'Localizaciones Celsig', 
          code: 'locations',
          icon: 'ri-map-pin-line',
          description: 'Gestión de localizaciones' 
        },
        { 
          id: 'delete_records', 
          name: 'Borrado Registros', 
          code: 'delete_records',
          icon: 'ri-delete-bin-line',
          description: 'Gestión de borrado de registros' 
        },
        // Nuevo módulo de Cargar Export agregado antes de Actividad
        { 
          id: 'cargar_export', 
          name: 'Cargar Export', 
          code: 'cargar_export',
          icon: 'ri-upload-line',
          description: 'Carga y gestión de archivos de exportación' 
        },
        // Nuevo módulo de Actividad agregado antes de Usuarios
        { 
          id: 'activity', 
          name: 'Actividad', 
          code: 'activity',
          icon: 'ri-line-chart-line',
          description: 'Monitoreo y registro de actividad del sistema' 
        },
        { 
          id: 'users', 
          name: 'Usuarios', 
          code: 'users',
          icon: 'ri-user-settings-line',
          description: 'Gestión de usuarios y permisos' 
        },
        { 
          id: 'admin', 
          name: 'Admin', 
          code: 'admin',
          icon: 'ri-admin-line',
          description: 'Panel de administración general' 
        }
      ],
      selectedPermissions: {},
      originalPermissions: {}
    };
  },
  computed: {
    allSelected() {
      return this.availableModules.every(module => this.selectedPermissions[module.id]);
    },
    permissionsChanged() {
      return this.availableModules.some(module => 
        this.selectedPermissions[module.id] !== this.originalPermissions[module.id]
      );
    }
  },
  watch: {
    userId: {
      handler(newId, oldId) {
        console.log(`userId cambió de ${oldId} a ${newId}`);
        if (newId !== oldId) {
          this.fetchUserPermissions();
        }
      }
    }
  },
  mounted() {
    this.fetchUserPermissions();
  },
  methods: {
    async fetchUserPermissions() {
      try {
        this.loading = true;
        this.error = null;
        
        // Reiniciar los permisos seleccionados y originales para evitar mezclar datos de diferentes usuarios
        this.selectedPermissions = {};
        this.originalPermissions = {};
        
        console.log(`Cargando permisos para usuario ID: ${this.userId}`);
        
        // Obtener el token de auth_token en localStorage
        const tokenJWT = localStorage.getItem('auth_token');
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        
        // Llamada para obtener info del usuario
        const userResponse = await axios.get(`${apiUrl}/users/${this.userId}`, {
          headers: {
            'Authorization': `Bearer ${tokenJWT}`,
            'Accept': 'application/json'
          }
        });
        
        this.user = userResponse.data.data;
        
        // Llamada para obtener los permisos del usuario
        const permissionsResponse = await axios.get(`${apiUrl}/users/${this.userId}/permissions`, {
          headers: {
            'Authorization': `Bearer ${tokenJWT}`,
            'Accept': 'application/json'
          }
        });
        
        const userPermissions = permissionsResponse.data.data || {};
        
        // Inicializar todos los permisos como false
        this.availableModules.forEach(module => {
          this.selectedPermissions[module.id] = false;
        });
        
        // Actualizar con los permisos que tenga asignados
        Object.keys(userPermissions).forEach(moduleId => {
          if (moduleId in this.selectedPermissions) {
            this.selectedPermissions[moduleId] = userPermissions[moduleId];
          }
        });
        
        // Guardar una copia de los permisos originales para comparar cambios
        this.originalPermissions = { ...this.selectedPermissions };
        
      } catch (error) {
        console.error("Error al obtener permisos de usuario:", error);
        this.error = error.response ? 
          `Error: ${error.response.status} - ${error.response.data.message || 'No se pudieron cargar los permisos'}` : 
          'Error de conexión al servidor';
      } finally {
        this.loading = false;
      }
    },
    
    toggleAllPermissions(event) {
      const value = event.target.checked;
      
      // Actualizar todos los permisos al mismo valor
      this.availableModules.forEach(module => {
        this.selectedPermissions[module.id] = value;
      });
      
      // No guardamos automáticamente todos los cambios para dar control al usuario
    },
    
    async updatePermission(moduleId) {
      try {
        this.saveMessage = null;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        const tokenJWT = localStorage.getItem('auth_token');
        
        console.log('Actualizando permiso:', {
          userId: this.userId,
          moduleId: moduleId,
          hasAccess: this.selectedPermissions[moduleId],
          url: `${apiUrl}/users/${this.userId}/permissions/${moduleId}`
        });
        
        const response = await axios.patch(`${apiUrl}/users/${this.userId}/permissions/${moduleId}`, {
          has_access: this.selectedPermissions[moduleId]
        }, {
          headers: {
            'Authorization': `Bearer ${tokenJWT}`,
            'Accept': 'application/json'
          }
        });
        
        console.log('Respuesta del servidor:', response.data);
        
        // Actualizar el valor original para seguimiento de cambios
        this.originalPermissions[moduleId] = this.selectedPermissions[moduleId];
        
        // Si el usuario actual está modificando sus propios permisos, actualizar el store
        if (Number(this.userId) === Number(this.$store.state.user?.id)) {
          console.log("Actualizando permisos en el store para usuario actual");
          
          // Obtener permisos actuales del store
          const currentPermissions = this.$store.state.user?.permissions || {};
          
          // Actualizar con el nuevo permiso
          const updatedPermissions = {
            ...currentPermissions,
            [moduleId]: this.selectedPermissions[moduleId]
          };
          
          // Guardar en el store
          this.$store.commit('setUserPermissions', updatedPermissions);
        }
        
        // Mostrar mensaje temporal
        this.$notify({
          title: 'Éxito',
          message: `Permiso ${this.selectedPermissions[moduleId] ? 'concedido' : 'retirado'} correctamente`,
          type: 'success',
          duration: 2000
        });
        
      } catch (error) {
        console.error("Error al actualizar permiso:", error);
        console.error("Error response:", error.response);
        console.error("Error status:", error.response?.status);
        console.error("Error data:", error.response?.data);
        
        // Restaurar el valor anterior en caso de error
        this.selectedPermissions[moduleId] = this.originalPermissions[moduleId];
        
        this.$notify({
          title: 'Error',
          message: 'No se pudo actualizar el permiso',
          type: 'error',
          duration: 3000
        });
      }
    },
    
    async saveAllPermissions() {
      try {
        this.saving = true;
        this.saveMessage = null;
        
        const apiUrl = process.env.VUE_APP_API_BASE_URL || 'http://teleconnect.test/api/v2';
        const tokenJWT = localStorage.getItem('auth_token');
        
        // Preparar objeto de permisos para enviar
        const permissionsToUpdate = {};
        
        this.availableModules.forEach(module => {
          permissionsToUpdate[module.id] = this.selectedPermissions[module.id];
        });
        
        // Enviar todos los permisos de una vez
        await axios.put(`${apiUrl}/users/${this.userId}/permissions`, {
          permissions: permissionsToUpdate
        }, {
          headers: {
            'Authorization': `Bearer ${tokenJWT}`,
            'Accept': 'application/json'
          }
        });
        
        // Actualizar permisos originales
        this.originalPermissions = { ...this.selectedPermissions };
        
        // Si el usuario actual está modificando sus propios permisos, actualizar el store
        if (Number(this.userId) === Number(this.$store.state.user?.id)) {
          console.log("Actualizando todos los permisos en el store para usuario actual");
          this.$store.commit('setUserPermissions', permissionsToUpdate);
        }
        
        this.saveMessage = "Todos los permisos se han guardado correctamente";
        this.saveSuccess = true;
        
        setTimeout(() => {
          this.saveMessage = null;
        }, 3000);
        
      } catch (error) {
        console.error("Error al guardar permisos:", error);
        this.saveMessage = error.response ? 
          (error.response.data.message || 'Error al guardar los permisos') : 
          'Error de conexión al servidor';
        this.saveSuccess = false;
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.permission-header {
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
}

.form-check-input:checked {
  background-color: #5cb85c;
  border-color: #5cb85c;
}

.form-check-input:focus {
  border-color: #5cb85c;
  box-shadow: 0 0 0 0.2rem rgba(92, 184, 92, 0.25);
}

.table th {
  font-size: 0.65rem;
  letter-spacing: 0.05rem;
}

.table td {
  padding: 12px 8px;
}

/* Estilo para el switch */
.form-switch .form-check-input {
  width: 2.5em;
}

/* Highlight para filas */
.table-hover tbody tr:hover {
  background-color: rgba(92, 184, 92, 0.05);
}
</style>